<template>
  <div class="glass-panel">
    <div class="p-6 border-b border-gray-200 dark:border-white/10">
      <div class="flex justify-between items-center">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Cost by User</h2>
        <div class="flex gap-3">
          <input 
            v-model="searchQuery"
            type="search" 
            placeholder="Search users..." 
            class="glass-input px-4 py-2 text-sm"
          />
          <button 
            @click="exportCSV"
            class="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>
    </div>
    
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-gray-200 dark:border-white/10">
            <th 
              @click="sort('name')"
              class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:text-purple-600"
            >
              User
              <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th 
              @click="sort('totalQueries')"
              class="text-right p-4 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:text-purple-600"
            >
              Total Queries
              <span v-if="sortKey === 'totalQueries'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th 
              @click="sort('totalCost')"
              class="text-right p-4 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:text-purple-600"
            >
              Total Cost
              <span v-if="sortKey === 'totalCost'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th 
              @click="sort('avgCostPerQuery')"
              class="text-right p-4 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:text-purple-600"
            >
              Avg Cost/Query
              <span v-if="sortKey === 'avgCostPerQuery'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="text-left p-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Last Active</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="user in paginatedUsers" 
            :key="user.id"
            class="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors cursor-pointer"
            @click="$emit('user-click', user)"
          >
            <td class="p-4">
              <div>
                <p class="font-medium text-gray-900 dark:text-white">{{ user.name }}</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ user.email }}</p>
              </div>
            </td>
            <td class="p-4 text-right">
              <span class="font-bold" :class="getQueryCountColor(user.totalQueries)">
                {{ user.totalQueries }}
              </span>
            </td>
            <td class="p-4 text-right">
              <span class="font-bold" :class="getTotalCostColor(user.totalCost)">
                {{ formatCurrency(user.totalCost) }}
              </span>
            </td>
            <td class="p-4 text-right">
              <span class="font-bold" :class="getAvgCostColor(user.avgCostPerQuery)">
                {{ formatCurrency(user.avgCostPerQuery) }}
              </span>
            </td>
            <td class="p-4 text-sm text-gray-600 dark:text-gray-400">{{ user.lastActive }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div class="p-4 border-t border-gray-200 dark:border-white/10 flex items-center justify-between">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Showing <span class="font-medium">{{ startIndex + 1 }}-{{ endIndex }}</span> of <span class="font-medium">{{ filteredUsers.length }}</span> users
      </p>
      <div class="flex items-center gap-2">
        <button 
          @click="currentPage--"
          :disabled="currentPage === 1"
          :class="currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-white/10'"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
        >
          Previous
        </button>
        <div class="flex gap-1">
          <button 
            v-for="page in visiblePages" 
            :key="page"
            @click="currentPage = page"
            :class="currentPage === page ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white shadow-lg shadow-purple-500/30' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          >
            {{ page }}
          </button>
        </div>
        <button 
          @click="currentPage++"
          :disabled="currentPage === totalPages"
          :class="currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-white/10'"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  users: Array<{
    id: number
    name: string
    email: string
    totalQueries: number
    totalCost: number
    avgCostPerQuery: number
    lastActive: string
  }>
}>()

const emit = defineEmits(['user-click'])

const { formatCurrency } = useCostFormatting()

const searchQuery = ref('')
const sortKey = ref<string>('totalCost')
const sortOrder = ref<'asc' | 'desc'>('desc')
const currentPage = ref(1)
const itemsPerPage = 20

const filteredUsers = computed(() => {
  let filtered = props.users
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(user => 
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    )
  }
  
  return filtered.sort((a, b) => {
    const aVal = a[sortKey.value as keyof typeof a]
    const bVal = b[sortKey.value as keyof typeof b]
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortOrder.value === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal)
    }
    
    return sortOrder.value === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number)
  })
})

const totalPages = computed(() => Math.ceil(filteredUsers.value.length / itemsPerPage))
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage)
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage, filteredUsers.value.length))

const paginatedUsers = computed(() => {
  return filteredUsers.value.slice(startIndex.value, endIndex.value)
})

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

const sort = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'desc'
  }
}

const getQueryCountColor = (count: number): string => {
  if (count < 100) return 'text-green-600 dark:text-green-400'
  if (count < 300) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

const getTotalCostColor = (cost: number): string => {
  if (cost < 50) return 'text-green-600 dark:text-green-400'
  if (cost < 150) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

const getAvgCostColor = (cost: number): string => {
  if (cost < 0.3) return 'text-green-600 dark:text-green-400'
  if (cost < 0.5) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

const exportCSV = () => {
  const headers = ['Name', 'Email', 'Total Queries', 'Total Cost', 'Avg Cost/Query', 'Last Active']
  const rows = filteredUsers.value.map(user => [
    user.name,
    user.email,
    user.totalQueries,
    user.totalCost,
    user.avgCostPerQuery,
    user.lastActive
  ])
  
  const csv = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n')
  
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'cost-by-user.csv'
  a.click()
  URL.revokeObjectURL(url)
}
</script>
